import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Crown, X } from "lucide-react";

import FoundingClaimModal from "./FoundingClaimModal";
import { trackEvent } from "../lib/api";
import { FOUNDING_TRIGGER_EVENT } from "../lib/foundingTrigger";
import { useAuth } from "../lib/auth";

// Once-per-session guard.
const SESSION_KEY = "blendsafe_founding_moment_seen_v1";
// Dismissal suppression — store an epoch-ms "show again after" timestamp so a
// dismissal (or CTA click) quiets the modal across sessions for 7 days.
const DISMISS_UNTIL_KEY = "blendsafe_founding_moment_dismiss_until";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
// Never interrupt the first page view: passive triggers (scroll/exit/fallback)
// are ignored for the first few seconds after mount.
const FIRST_VIEW_GRACE_MS = 5000;
// 50–60% scroll depth.
const SCROLL_DEPTH_THRESHOLD = 0.55;
// Fallback after ~50s of ACTIVE engagement (only counts while tab is visible).
const FALLBACK_ACTIVE_MS = 50000;

function isSuppressed() {
  try {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
    const until = parseInt(localStorage.getItem(DISMISS_UNTIL_KEY) || "0", 10);
    if (until && Date.now() < until) return true;
  } catch {
    /* storage blocked — fall through (treat as not suppressed) */
  }
  return false;
}

/**
 * FoundingMomentDialog — intent-driven Founding 100 reveal.
 *
 * Mounted ONCE globally (App shell). It no longer auto-pops on arrival.
 * Trigger hierarchy:
 *   P1 (high intent) — fires immediately on `founding-trigger` events
 *      (successful waitlist signup, successful check result).
 *   P2 — 50–60% scroll depth.
 *   P3 — desktop exit intent (cursor leaves top viewport; never on mobile).
 *   Fallback — after ~50s of active (tab-visible) engagement.
 *
 * Guarantees: max once per session, 7-day dismissal suppression, never within
 * the first few seconds of the first page view, no exit-intent on mobile, and
 * impressions/dismissals/CTA-clicks/conversions tracked as separate events.
 *
 * @param {boolean} passiveTriggersEnabled — when false (e.g. admin routes),
 *   only high-intent events can open the modal; scroll/exit/fallback are off.
 */
export default function FoundingMomentDialog({ passiveTriggersEnabled = true }) {
  const { user } = useAuth();
  // Never surface the founding offer to people who already have Pro or a
  // founding claim — it contradicts their status.
  const isPaidUser = !!(user?.is_pro || user?.is_founding);
  const [open, setOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const firedRef = useRef(false);
  const ctaClickedRef = useRef(false);
  // Trigger that surfaced the modal — attached to CTA/dismiss/conversion so
  // the admin funnel can attribute the whole journey to one trigger.
  const openedTriggerRef = useRef(null);

  useEffect(() => {
    if (isPaidUser) return undefined;
    if (isSuppressed()) return undefined;

    const mountedAt = Date.now();
    let activeMs = 0;
    let activeTimer = null;

    const fire = (reason) => {
      if (firedRef.current || isSuppressed()) return;
      firedRef.current = true;
      openedTriggerRef.current = reason;
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* noop */ }
      cleanup();
      setOpen(true);
      trackEvent("founding_modal_impression", { trigger: reason });
    };

    // ---- P1: high-intent external events (signup / check result) ----------
    const onTrigger = (e) => fire((e && e.detail && e.detail.reason) || "event");
    window.addEventListener(FOUNDING_TRIGGER_EVENT, onTrigger);

    let onScroll = null;
    let onMouseOut = null;
    const pastGrace = () => Date.now() - mountedAt >= FIRST_VIEW_GRACE_MS;

    if (passiveTriggersEnabled) {
      // ---- P2: scroll depth ----------------------------------------------
      onScroll = () => {
        if (!pastGrace()) return;
        const scrolled = window.scrollY || document.documentElement.scrollTop || 0;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0 && scrolled / max >= SCROLL_DEPTH_THRESHOLD) fire("scroll_depth");
      };
      window.addEventListener("scroll", onScroll, { passive: true });

      // ---- P3: desktop exit intent (never on mobile / touch) -------------
      const isDesktop = window.matchMedia(
        "(min-width: 768px) and (pointer: fine)",
      ).matches;
      if (isDesktop) {
        onMouseOut = (e) => {
          if (!pastGrace()) return;
          // Cursor leaving through the top edge of the viewport.
          if (e.clientY <= 0 && !e.relatedTarget) fire("exit_intent");
        };
        document.addEventListener("mouseout", onMouseOut);
      }

      // ---- Fallback: active engagement timer -----------------------------
      activeTimer = setInterval(() => {
        if (document.visibilityState === "visible") activeMs += 1000;
        if (activeMs >= FALLBACK_ACTIVE_MS) fire("active_engagement_fallback");
      }, 1000);
    }

    function cleanup() {
      window.removeEventListener(FOUNDING_TRIGGER_EVENT, onTrigger);
      if (onScroll) window.removeEventListener("scroll", onScroll);
      if (onMouseOut) document.removeEventListener("mouseout", onMouseOut);
      if (activeTimer) clearInterval(activeTimer);
    }
    return cleanup;
  }, [passiveTriggersEnabled, isPaidUser]);

  // Quiet the modal for 7 days (across sessions) after any close.
  const suppressForWeek = () => {
    try {
      localStorage.setItem(DISMISS_UNTIL_KEY, String(Date.now() + SEVEN_DAYS_MS));
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch { /* noop */ }
  };

  // Explicit user dismissal (Maybe later button). Calling setOpen(false)
  // directly does NOT invoke Radix's onOpenChange, so we track + suppress here.
  const dismiss = (reason) => {
    suppressForWeek();
    trackEvent("founding_modal_dismiss", { reason, trigger: openedTriggerRef.current });
    setOpen(false);
  };

  // Radix-driven closes only: X button, ESC, outside click.
  const handleOpenChange = (next) => {
    if (!next && open) {
      if (ctaClickedRef.current) {
        ctaClickedRef.current = false; // CTA already tracked + suppressed.
      } else {
        suppressForWeek();
        trackEvent("founding_modal_dismiss", {
          reason: "overlay_or_esc",
          trigger: openedTriggerRef.current,
        });
      }
    }
    setOpen(next);
  };

  const onReserve = () => {
    ctaClickedRef.current = true;
    suppressForWeek();
    trackEvent("founding_modal_cta_click", { trigger: openedTriggerRef.current });
    setOpen(false);
    // Defer claim open so the close animation finishes.
    setTimeout(() => setClaimOpen(true), 180);
  };

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-50 bg-[#1f3d2e]/30 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <DialogPrimitive.Content
            data-testid="founding-moment-dialog"
            className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[rgba(168,122,50,0.22)] bg-cream-50 p-6 sm:p-7 shadow-floating duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            style={{
              boxShadow:
                "0 1px 2px rgba(0,0,0,0.04), 0 28px 64px -22px rgba(31,61,46,0.40), 0 14px 32px -18px rgba(168,122,50,0.22)",
            }}
          >
            <div className="flex items-center justify-between">
              <span
                data-testid="founding-moment-badge"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.22em] text-[#a87a32]"
                style={{
                  background: "linear-gradient(135deg,#f4e8d0 0%,#e8d4a8 100%)",
                  border: "1px solid rgba(168,122,50,0.3)",
                }}
              >
                <Crown className="w-3 h-3" /> Founding member
              </span>
              <DialogPrimitive.Close
                data-testid="founding-moment-close"
                aria-label="Close"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-ink-mute hover:text-ink hover:bg-cream-100 transition-colors focus:outline-none focus:ring-2 focus:ring-ink/20"
              >
                <X className="w-4 h-4" />
              </DialogPrimitive.Close>
            </div>

            <DialogPrimitive.Title
              data-testid="founding-moment-headline"
              className="mt-4 text-[22px] sm:text-[24px] font-extrabold tracking-[-0.018em] leading-[1.15] text-ink"
            >
              Become a founding member
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-2 text-[13.5px] text-ink-soft leading-snug">
              Pro free for 12 months when payments launch.
            </DialogPrimitive.Description>

            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <button
                type="button"
                data-testid="founding-moment-reserve"
                onClick={onReserve}
                className="h-11 px-5 rounded-xl text-cream-50 text-[13.5px] font-semibold transition-all hover:scale-[1.01] active:scale-[0.99] inline-flex items-center justify-center gap-2 flex-1"
                style={{
                  background:
                    "linear-gradient(135deg,#1f3d2e 0%,#2c5240 50%,#1f3d2e 100%)",
                  boxShadow:
                    "0 1px 2px rgba(0,0,0,0.06), 0 12px 24px -10px rgba(31,61,46,0.45)",
                }}
              >
                Reserve my spot
              </button>
              <button
                type="button"
                data-testid="founding-moment-later"
                onClick={() => dismiss("maybe_later")}
                className="h-11 px-5 rounded-xl text-[13.5px] font-medium text-ink-soft hover:text-ink hover:bg-cream-100 transition-colors inline-flex items-center justify-center"
              >
                Maybe later
              </button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <FoundingClaimModal
        open={claimOpen}
        onOpenChange={setClaimOpen}
        onSuccess={() =>
          trackEvent("founding_modal_conversion", {
            trigger: openedTriggerRef.current,
            from: "founding_moment",
          })
        }
      />
    </>
  );
}
